// Sparkline (RESTYLE §2, §3): an inline SVG of a short series, e.g. events per second over the last 60 s.
//   <Sparkline values label width? height? max? tone? dot? baseline? className? />
//       a 1.5px line over a 10% area in the live colour (tone "ink" for neutral series); a dot marks the latest value.
//       label is the accessible name ("Events per second, last 60 s: 3 now, peak 12"). max defaults to the series peak.
//       Values are plotted left to right, oldest first; fewer than two values draw a flat baseline.
import { cx } from "./cx";

export function Sparkline({
  values,
  label,
  width = 120,
  height = 28,
  max,
  tone = "live",
  dot = true,
  baseline = true,
  className,
}: {
  values: readonly number[];
  label: string;
  width?: number;
  height?: number;
  max?: number;
  tone?: "live" | "ink";
  dot?: boolean;
  baseline?: boolean;
  className?: string;
}) {
  const pad = 2.5;
  const w = Math.max(8, width);
  const h = Math.max(8, height);
  const floor = h - pad;
  const top = Math.max(1e-9, max ?? Math.max(0, ...values));
  const n = values.length;
  const pts = n >= 2 ? values.map((v, i) => [pad + (i / (n - 1)) * (w - 2 * pad), floor - (Math.max(0, Math.min(v, top)) / top) * (h - 2 * pad)] as const) : [];
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)}`).join("");
  const last = pts[pts.length - 1];
  return (
    <svg className={cx("spark", tone === "ink" && "spark--ink", className)} width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
      {baseline && <line className="bl" x1={pad} x2={w - pad} y1={floor + 0.5} y2={floor + 0.5} />}
      {pts.length > 0 && (
        <>
          <path className="ar" d={`${line}L${(w - pad).toFixed(2)} ${floor}L${pad} ${floor}Z`} />
          <path className="ln" d={line} />
          {dot && last && <circle className="dt" cx={last[0]} cy={last[1]} r={2.25} />}
        </>
      )}
    </svg>
  );
}
